import { theme } from '@Guidelines/Theme/theme';
import resolveIdentityTint from '@Guidelines/Theme/helpers/resolveIdentityTint';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import { css } from '@emotion/react';
import type * as React from 'react';

type Props = {
	readonly participants: readonly InternalInterviewParticipantView[];
};

const listStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const badgeStyle = (surface: string, ink: string) => css`
	padding: 0.375rem 0.75rem;
	border-radius: ${theme.radii.pill};
	background-color: ${surface};
	color: ${ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.2;
`;

const emptyStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const visuallyHiddenStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
`;

const MENTION_BY_KIND: Readonly<Record<InternalInterviewParticipantView['kind'], string>> = Object.freeze({
	contact: 'Contact du dossier',
	staff: 'Membre du personnel',
});

/**
 * One tinted pill per participant. `kind` and `id` ranges overlap across the two referentials, so the
 * React key combines both while the tint seed takes `id` alone, like the identity tiles do.
 */
const InterviewParticipantBadges: React.FC<Props> = ({ participants }) => {
	if (participants.length === 0) {
		return <p css={emptyStyle}>Participants non renseignés</p>;
	}

	return (
		<ul aria-label="Participants" css={listStyle}>
			{participants.map((participant) => {
				const tint = resolveIdentityTint(participant.id);

				return (
					<li
						key={`${participant.kind}-${participant.id}`}
						css={badgeStyle(tint.surface, tint.ink)}
					>
						{participant.label}
						<span css={visuallyHiddenStyle}>{`, ${MENTION_BY_KIND[participant.kind]}`}</span>
					</li>
				);
			})}
		</ul>
	);
};

export default InterviewParticipantBadges;
